import React, { useEffect, useRef } from 'react';
import { MarketplaceBuyerProfile } from '../../../slices/marketplace/buyer/models/buyer-profile';
import * as yup from 'yup';
import ButtonLoader from '../../Layout/Buttons/ButtonLoader';
import { Form, Input, Label } from '@availity/form';
import { Col, Row } from 'reactstrap';
import { PrefilledTags } from '../../../models/PrefilledTags/PrefilledTags';
import { DocumentTagEnum } from '../../../models/PrefilledTags/DocumentTagEnum';

interface DataNdaLegalDocumentInfo {
    fullCorporateLegalName: string;
    legalEntityStateOfRegistration: string;
    legalEntityCityOfRegistration: string;
    legalEntityZipCodeOfRegistration: string;
    legalEntityAddressOfRegistration: string;
    dbaNickName: string;
    contactName: string;
    contactTitle: string;
    legalEntityType: string;
}

interface DataNdaLegalInformationFormProps {
    onClose(): void;
    onSubmit(prefilledTags: PrefilledTags[]): void;
    isLoading?: boolean;
    buyerProfile: MarketplaceBuyerProfile;
    opportunityId: number;
}
export const DataNdaLegalInformationForm = (
    props: DataNdaLegalInformationFormProps
) => {
    let formRef = useRef(null);

    useEffect(() => {
        if (formRef) {
            initializeForm();
        }
    }, [formRef]);

    const initializeForm = () => {
        const addressContainer = [
            props.buyerProfile?.legalDocumentInputs?.addressLine1 ?? '',
            props.buyerProfile?.legalDocumentInputs?.addressLine2 ?? '',
        ];
        const composedAddress = addressContainer.filter((f) => f).join(', ');

        const legalDocumentInfo: DataNdaLegalDocumentInfo = {
            fullCorporateLegalName:
                props.buyerProfile?.legalDocumentInputs
                    ?.fullCorporateLegalName ?? '',
            legalEntityStateOfRegistration:
                props.buyerProfile?.legalDocumentInputs
                    ?.legalEntityStateOfRegistration ?? '',
            legalEntityZipCodeOfRegistration:
                props.buyerProfile?.legalDocumentInputs?.zipCode ?? '',
            legalEntityCityOfRegistration:
                props.buyerProfile?.legalDocumentInputs?.city ?? '',
            legalEntityAddressOfRegistration: composedAddress,
            dbaNickName: props.buyerProfile?.legalDocumentInputs?.dbaNickName,
            contactName:
                props.buyerProfile?.legalDocumentInputs?.contactName ?? '',
            contactTitle:
                props.buyerProfile?.legalDocumentInputs?.contactTitle ?? '',
            legalEntityType:
                props.buyerProfile?.legalDocumentInputs?.typeOfLegalEntity ??
                '',
        };

        formRef?.current?.setFieldValue(
            'fullCorporateLegalName',
            legalDocumentInfo.fullCorporateLegalName,
            false
        );
        formRef?.current?.setFieldValue(
            'legalEntityStateOfRegistration',
            legalDocumentInfo.legalEntityStateOfRegistration,
            false
        );
        formRef?.current?.setFieldValue(
            'dbaNickName',
            legalDocumentInfo.dbaNickName,
            false
        );
        formRef?.current?.setFieldValue(
            'legalEntityAddressOfRegistration',
            legalDocumentInfo.legalEntityAddressOfRegistration,
            false
        );
        formRef?.current?.setFieldValue(
            'legalEntityZipCodeOfRegistration',
            legalDocumentInfo.legalEntityZipCodeOfRegistration,
            false
        );
        formRef?.current?.setFieldValue(
            'legalEntityCityOfRegistration',
            legalDocumentInfo.legalEntityCityOfRegistration,
            false
        );
        formRef?.current?.setFieldValue(
            'contactName',
            legalDocumentInfo.contactName,
            false
        );
        formRef?.current?.setFieldValue(
            'contactTitle',
            legalDocumentInfo.contactTitle,
            false
        );
        formRef?.current?.setFieldValue(
            'legalEntityType',
            legalDocumentInfo.legalEntityType,
            false
        );
    };

    const mapToPrefilledTags = (documentInfo: DataNdaLegalDocumentInfo) => {
        return [
            {
                tagEnum: DocumentTagEnum.BuyerLegalName,
                value: documentInfo.fullCorporateLegalName,
            },
            {
                tagEnum: DocumentTagEnum.BuyerAddressOfRegistration,
                value: documentInfo.legalEntityAddressOfRegistration,
            },
            {
                tagEnum: DocumentTagEnum.BuyerZipCodeOfRegistration,
                value: documentInfo.legalEntityZipCodeOfRegistration,
            },
            {
                tagEnum: DocumentTagEnum.BuyerCityOfRegistration,
                value: documentInfo.legalEntityCityOfRegistration,
            },
            {
                tagEnum: DocumentTagEnum.BuyerStateOfRegistration,
                value: documentInfo.legalEntityStateOfRegistration,
            },
            {
                tagEnum: DocumentTagEnum.BuyerLegalContactName,
                value: documentInfo.contactName,
            },
            {
                tagEnum: DocumentTagEnum.BuyerLegalContactTitle,
                value: documentInfo.contactTitle,
            },
            {
                tagEnum: DocumentTagEnum.BuyerDbaNickName,
                value: documentInfo.dbaNickName,
            },
            {
                tagEnum: DocumentTagEnum.OppId,
                value: props.opportunityId.toString(),
            },
            {
                tagEnum: DocumentTagEnum.BuyerTypeOfLegalEntity,
                value: documentInfo.legalEntityType,
            },
        ];
    };

    return (
        <Form
            innerRef={formRef}
            initialValues={{
                fullCorporateLegalName: '',
                legalEntityStateOfRegistration: '',
                legalEntityCityOfRegistration: '',
                legalEntityZipCodeOfRegistration: '',
                legalEntityAddressOfRegistration: '',
                legalEntityType: '',
                dbaNickName: '',
                contactName: '',
                contactTitle: '',
            }}
            validationSchema={yup.object().shape({
                fullCorporateLegalName: yup.string().trim().required(),
                legalEntityStateOfRegistration: yup.string().trim().required(),
                legalEntityCityOfRegistration: yup.string().trim().required(),
                legalEntityZipCodeOfRegistration: yup
                    .string()
                    .trim()
                    .required(),
                legalEntityAddressOfRegistration: yup
                    .string()
                    .trim()
                    .required(),
                dbaNickName: yup.string().trim().required(),
                contactName: yup.string().trim().required(),
                contactTitle: yup.string().trim().required(),
                legalEntityType: yup.string().trim().required(),
            })}
            onSubmit={(values) => {
                const trimmedValues = {
                    fullCorporateLegalName:
                        values.fullCorporateLegalName.trim(),
                    legalEntityStateOfRegistration:
                        values.legalEntityStateOfRegistration.trim(),
                    legalEntityCityOfRegistration:
                        values.legalEntityCityOfRegistration.trim(),
                    legalEntityZipCodeOfRegistration:
                        values.legalEntityZipCodeOfRegistration.trim(),
                    legalEntityAddressOfRegistration:
                        values.legalEntityAddressOfRegistration.trim(),
                    dbaNickName: values.dbaNickName.trim(),
                    contactName: values.contactName.trim(),
                    contactTitle: values.contactTitle.trim(),
                    legalEntityType: values.legalEntityType.trim(),
                };

                const tags = mapToPrefilledTags(trimmedValues);
                props.onSubmit(tags);
            }}>
            <div className="rows-container">
                <Row>
                    <Col md={6}>
                        <Label>{'Full Corporate Legal Name'}</Label>
                        <Input name="fullCorporateLegalName"></Input>
                    </Col>
                    <Col md={6}>
                        <Label>{'DBA Nickname'}</Label>
                        <Input name="dbaNickName"></Input>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Label>{'Legal Entity State of Registration'}</Label>
                        <Input name="legalEntityStateOfRegistration"></Input>
                    </Col>
                    <Col md={6}>
                        <Label>{'Legal Entity City of Registration'}</Label>
                        <Input name="legalEntityCityOfRegistration"></Input>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Label>{'Legal Entity Address of Registration'}</Label>
                        <Input name="legalEntityAddressOfRegistration"></Input>
                    </Col>
                    <Col md={6}>
                        <Label>{'Legal Entity Zip Code of Registration'}</Label>
                        <Input name="legalEntityZipCodeOfRegistration"></Input>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Label>{'Type of Legal Entity:'}</Label>
                        <Input name="legalEntityType"></Input>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <strong className="section-divider">
                            {'Signatory'}
                        </strong>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Label>{'Contact Name'}</Label>
                        <Input name="contactName"></Input>
                    </Col>
                    <Col md={6}>
                        <Label>{'Contact Title'}</Label>
                        <Input name="contactTitle"></Input>
                    </Col>
                </Row>
                <div className="buttons-wrapper">
                    <button
                        className="btn btn-cancel"
                        onClick={() => {
                            props.onClose();
                        }}
                        disabled={props.isLoading}>
                        {'Cancel'}
                    </button>
                    <ButtonLoader
                        disabled={props.isLoading}
                        isLoading={props.isLoading}
                        className="btn btn-primary"
                        buttonText={'Confirm'}
                        loaderButtonText={''}
                        onClick={() => {}}></ButtonLoader>
                </div>
            </div>
        </Form>
    );
};
